export const AXIS_ORIGIN_X = 73.392;
export const AXIS_ORIGIN_Y = 69.645;

/** term_id 编码：年级 = term_id / 2 */
export function getGradeFromTermId(termId) {
    if (termId == null || termId === '') return null;
    const grade = Number(termId) / 2;
    if (Number.isNaN(grade) || grade <= 0) return null;
    return grade;
}

export function resolveQuadrantConfig() {
    return {
        k0: 1,
        axisX: AXIS_ORIGIN_X,
        axisY: AXIS_ORIGIN_Y,
    };
}

export function resolveItemGrade(item, fallbackGradeValue) {
    const fromTerm = getGradeFromTermId(item && item.term_id);
    if (fromTerm != null) return fromTerm;
    if (fallbackGradeValue != null && fallbackGradeValue !== '') {
        return fallbackGradeValue;
    }
    return null;
}

export function scalePoint(x, y) {
    const nx = Number(x);
    const ny = Number(y);
    if (Number.isNaN(nx) || Number.isNaN(ny)) return null;
    return { x: nx, y: ny, k0: 1 };
}

const CN_GRADE_NUM = { 一: 1, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 };

/** 年级键：1、"1"、"1年级"、"一年级" 都归成 1-9 */
export function normalizeGradeKey(gradeKey) {
    if (gradeKey == null || gradeKey === '') return null;
    const text = String(gradeKey).trim();
    const asNum = Number(text);
    if (Number.isFinite(asNum) && asNum >= 1 && asNum <= 9 && String(asNum) === text) return asNum;
    const digit = text.match(/(\d+)\s*年级/);
    if (digit) {
        const n = Number(digit[1]);
        if (n >= 1 && n <= 9) return n;
    }
    const cn = text.match(/([一二三四五六七八九])年级/);
    if (cn && CN_GRADE_NUM[cn[1]]) return CN_GRADE_NUM[cn[1]];
    if (Number.isFinite(asNum) && asNum >= 1 && asNum <= 9) return asNum;
    return null;
}

/**
 * ownexam.term_id 是学期序号：1、2 为一年级，3、4 为二年级，以此类推。
 * 大于 18 的值不是这套序号，返回 null。
 */
export function gradeKeyFromOwnexamTermId(termId) {
    const n = Number(termId);
    if (!Number.isFinite(n) || n <= 0 || n > 18) return null;
    const grade = Math.ceil(n / 2);
    if (grade < 1 || grade > 9) return null;
    return grade;
}

/** 个人报告散点年级：优先学期序号，其次试卷名或点上的年级，最后用页面兜底年级 */
export function resolveOwnexamGradeKey(item, fallbackGrade) {
    const fromTerm = gradeKeyFromOwnexamTermId(item && item.term_id);
    if (fromTerm != null) return fromTerm;
    const fromName = normalizeGradeKey(item && item.name);
    if (fromName != null) return fromName;
    const fromItem = normalizeGradeKey(item && (item.grade != null ? item.grade : item.gradeId));
    if (fromItem != null) return fromItem;
    return normalizeGradeKey(fallbackGrade);
}

/** ratioFactorMap.data：{ "1": "1", "2": "1.071", ... }，缺年级时系数为 1 */
export function lookupGradeK0(factorMap, gradeKey) {
    if (factorMap == null || gradeKey == null || gradeKey === '') return 1;
    const normalized = normalizeGradeKey(gradeKey);
    const key = normalized != null ? String(normalized) : String(gradeKey);
    const raw = factorMap[key] != null ? factorMap[key] : factorMap[Number(key)];
    const k0 = Number(raw);
    if (!Number.isFinite(k0) || k0 <= 0) return 1;
    return k0;
}

/** 班级象限：维度现算的 x、y 再乘年级 k0 */
export function scaleRawXyByGradeK0(x, y, gradeKey, factorMap) {
    const point = scalePoint(x, y);
    if (!point) return null;
    const k0 = lookupGradeK0(factorMap, gradeKey);
    return { x: point.x * k0, y: point.y * k0, k0 };
}

function scaleItemXyByK0(item, k0) {
    if (!item) return item;
    const x = Number(item.x);
    const y = Number(item.y);
    const factor = Number.isFinite(k0) && k0 > 0 ? k0 : 1;
    return {
        ...item,
        x: Number.isNaN(x) ? item.x : x * factor,
        y: Number.isNaN(y) ? item.y : y * factor,
    };
}

/**
 * 各年级象限散点：按年级把 x、y 乘以 k0。
 * raw 为 { 年级: 点列表 }；数组形态没有年级键时，尝试用点上的 grade / gradeId。
 */
export function applyQuadrantRatioFactor(raw, factorMap) {
    const scaleList = (list, gradeKey) => {
        if (!Array.isArray(list)) return [];
        return list.map((item) => {
            const grade = gradeKey != null && gradeKey !== ''
                ? gradeKey
                : (item && (item.grade != null ? item.grade : item.gradeId));
            return scaleItemXyByK0(item, lookupGradeK0(factorMap, grade));
        });
    };
    if (Array.isArray(raw)) {
        return raw.map((list) => scaleList(list));
    }
    const result = {};
    Object.keys(raw || {}).forEach((key) => {
        result[key] = scaleList(raw[key], key);
    });
    return result;
}

/**
 * 散点图轴范围：覆盖全部点 + 原点十字线，再留 padding，避免低/高分点被裁出视野。
 * scatterData 项需含 value: [x, y]
 */
export function resolveAxisExtent(scatterData, axisX, axisY) {
    let min = Math.min(40, Number(axisX) || 40, Number(axisY) || 40);
    let max = 100;
    (scatterData || []).forEach((p) => {
        const x = Number(p && p.value && p.value[0]);
        const y = Number(p && p.value && p.value[1]);
        if (!Number.isNaN(x)) {
            min = Math.min(min, x);
            max = Math.max(max, x);
        }
        if (!Number.isNaN(y)) {
            min = Math.min(min, y);
            max = Math.max(max, y);
        }
    });
    const pad = 5;
    min = Math.floor(min - pad);
    max = Math.ceil(max + pad);
    if (min >= max) {
        min = 0;
        max = 100;
    }
    return { min, max };
}

/** 教师端四象限：由维度分合成努力/天赋原始值 */
export function buildEffortTalentFromStuItem(item) {
    if (!item) return null;
    const rawX = (Number(item.centerPos) + Number(item.gridSize) + Number(item.comboRel) + Number(item.multiDist)) / 4;
    const rawY = (Number(item.glyphGrav) + Number(item.strokeRel) + Number(item.rewindRel)) / 3;
    if (Number.isNaN(rawX) || Number.isNaN(rawY)) return null;
    return { rawX, rawY };
}

/** 教师端四象限散点：stuList → ECharts scatter data */
export function buildQuadrantScatterFromStuList(stuList, gradeValue) {
    return (stuList || []).reduce((acc, item) => {
        const point = buildEffortTalentFromStuItem(item);
        if (!point) return acc;
        const scaled = scalePoint(point.rawX, point.rawY, gradeValue);
        if (!scaled) return acc;
        acc.push({
            name: item.stuName,
            value: [scaled.x, scaled.y],
        });
        return acc;
    }, []);
}

export function buildMarkLineData(axisX, axisY) {
    const xLabel = Number(axisX);
    const yLabel = Number(axisY);
    return [
        { xAxis: axisX, label: { show: true, position: 'start', formatter: `大众型 ${xLabel}` } },
        { yAxis: axisY, label: { show: true, position: 'start', formatter: `自然成长 ${yLabel}` } },
        { xAxis: axisX, label: { show: true, position: 'end', formatter: '天赋型' } },
        { yAxis: axisY, label: { show: true, position: 'end', formatter: '笃行不倦' } },
    ];
}

/** 返回象限序号 1-4，无法判定时返回 null */
export function classifyQuadrantIndex(x, y) {
    const point = scalePoint(x, y);
    if (!point) return null;
    const { axisX, axisY } = resolveQuadrantConfig();
    const { x: sx, y: sy } = point;
    if (sx >= axisX && sy >= axisY) return 1;
    if (sx < axisX && sy >= axisY) return 2;
    if (sx < axisX && sy < axisY) return 3;
    if (sx >= axisX && sy < axisY) return 4;
    return null;
}

/** 矩阵图解读：象限位置名（与文案正文一致） */
export const MATRIX_QUAD_NAMES = {
    1: '第一象限（右上）',
    2: '第二象限（左上）',
    3: '第三象限（左下）',
    4: '第四象限（右下）',
};

export const EMPTY_MATRIX_INTERP = {
    total: '—',
    maxName: '—',
    maxRatio: '—',
    minName: '—',
    minRatio: '—',
    q1: { count: '—', ratio: '—' },
    q2: { count: '—', ratio: '—' },
    q3: { count: '—', ratio: '—' },
    q4: { count: '—', ratio: '—' },
};

function joinQuadNames(indices) {
    const names = indices.map((i) => MATRIX_QUAD_NAMES[i]).filter(Boolean);
    if (names.length <= 1) return names[0] || '';
    if (names.length === 2) return `${names[0]}和${names[1]}`;
    return `${names.slice(0, -1).join('、')}和${names[names.length - 1]}`;
}

/**
 * 按散点图同一套规则（x/y + 固定原点）统计矩阵图解读字段。
 * 最高/最低象限人数并列时名称用「A和B」拼接，比例取该并列值。
 */
export function buildMatrixInterpFromList(list) {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0 };
    let total = 0;
    (list || []).forEach((item) => {
        if (!item) return;
        const q = classifyQuadrantIndex(item.x, item.y);
        if (q == null) return;
        counts[q] += 1;
        total += 1;
    });
    if (total === 0) {
        return {
            ...EMPTY_MATRIX_INTERP,
            q1: { ...EMPTY_MATRIX_INTERP.q1 },
            q2: { ...EMPTY_MATRIX_INTERP.q2 },
            q3: { ...EMPTY_MATRIX_INTERP.q3 },
            q4: { ...EMPTY_MATRIX_INTERP.q4 },
        };
    }
    const ratioOf = (c) => ((c / total) * 100).toFixed(2);
    const entries = [1, 2, 3, 4].map((i) => ({
        i,
        count: counts[i],
        ratio: ratioOf(counts[i]),
    }));
    const maxCount = Math.max(counts[1], counts[2], counts[3], counts[4]);
    const minCount = Math.min(counts[1], counts[2], counts[3], counts[4]);
    const maxOnes = entries.filter((e) => e.count === maxCount);
    const minOnes = entries.filter((e) => e.count === minCount);
    return {
        total,
        maxName: joinQuadNames(maxOnes.map((e) => e.i)),
        maxRatio: maxOnes[0].ratio,
        minName: joinQuadNames(minOnes.map((e) => e.i)),
        minRatio: minOnes[0].ratio,
        q1: { count: counts[1], ratio: ratioOf(counts[1]) },
        q2: { count: counts[2], ratio: ratioOf(counts[2]) },
        q3: { count: counts[3], ratio: ratioOf(counts[3]) },
        q4: { count: counts[4], ratio: ratioOf(counts[4]) },
    };
}

/** 个人报告象限图解读：四象限一句建议 */
export const PERSONAL_QUAD_ADVICE = {
    1: '结构与规范书写能力在平均水平以上。建议保持练习，可担任书写示范。',
    2: '笔画、重心、收放较好，但字体不够工整。建议加强格子意识，练习“笔画定位法”，多临摹田字格范字。',
    3: '基础薄弱，存在偏大偏小、歪斜等共性问题。建议从基本笔画和占格开始系统训练，每天15分钟“慢写”纠错。',
    4: '结构准确，但笔画、重心、收放不够精致，字显呆板。建议重点练控笔和笔画粗细变化，多进行“快写+对比”练习，提升灵动感。',
};

/** 班级报告象限图解读：各象限固定「特点 / 建议」 */
export const CLASS_QUAD_COPY = {
    1: {
        trait: '笔画与规范书写能力均在平均水平以上，书写基础扎实、习惯良好，是班级书写的榜样力量。',
        advice: '保持日常练习量，重点转向书写速度与精致度的平衡训练（如限时书写挑战）；同时安排担任班级“书写小老师”，通过教别人巩固自身能力，并带动全班书写水平提升。',
    },
    2: {
        trait: '笔画质量、重心把控、收放关系较好，有一定的书写天赋和领悟力，但字体不够工整，占格和结构稳定性不足。',
        advice: '采用“笔画定位法”——写前先用眼睛量出起笔位置和四周留白（约格子边长的六分之一），多临摹田字格范字，逐步建立“格子意识”；同时保持y维度的优势练习，避免顾此失彼。',
    },
    3: {
        trait: '书写基础相对薄弱，存在偏大偏小、歪斜、占格不居中等共性问题，是教学中需重点关注和扶持的群体。',
        advice: '从基本笔画和占格开始系统训练，每天15分钟“慢写”纠错，每次只盯一个目标（第1周练占格，第2周练重心，第3周练大小），降低难度、小步快走，帮助建立信心；同时与第一象限学生结对，通过观察模仿加速进步。',
    },
    4: {
        trait: '能做到基本的规范书写，但笔画质感、重心稳定、收放节奏不够精致，字显呆板、缺少灵气。',
        advice: '在保持现有书写水平的基础上，重点练习“收放关系”——每天写前先找主笔，让主笔明显伸长，其他笔画收紧，形成张弛对比；同时结合古诗或短句进行“快写+对比”练习（先慢写一遍找问题，再提速写一遍，对比差异），逐步提升书写节奏感和灵动性。',
    },
};

/**
 * 班级矩形图解读：与 fourQuadrants 图同一套维度现算 x/y，按接口顺序收集名单。
 * total=0 时返回 null（整块不展示）。
 */
export function buildClassMatrixInterpFromStuList(stuList, gradeKey, factorMap) {
    const buckets = { 1: [], 2: [], 3: [], 4: [] };
    (stuList || []).forEach((item) => {
        if (!item) return;
        const point = buildEffortTalentFromStuItem(item);
        if (!point) return;
        const scaled = scaleRawXyByGradeK0(point.rawX, point.rawY, gradeKey, factorMap);
        if (!scaled) return;
        const q = classifyQuadrantIndex(scaled.x, scaled.y);
        if (q == null) return;
        buckets[q].push(item.stuName || '');
    });
    const counts = {
        1: buckets[1].length,
        2: buckets[2].length,
        3: buckets[3].length,
        4: buckets[4].length,
    };
    const total = counts[1] + counts[2] + counts[3] + counts[4];
    if (total === 0) return null;

    const ratioOf = (c) => ((c / total) * 100).toFixed(2);
    const entries = [1, 2, 3, 4].map((i) => ({
        i,
        count: counts[i],
        ratio: ratioOf(counts[i]),
    }));
    const maxCount = Math.max(counts[1], counts[2], counts[3], counts[4]);
    const minCount = Math.min(counts[1], counts[2], counts[3], counts[4]);
    const maxOnes = entries.filter((e) => e.count === maxCount);
    const minOnes = entries.filter((e) => e.count === minCount);
    const makeQuad = (i) => {
        const copy = CLASS_QUAD_COPY[i];
        const names = buckets[i].filter(Boolean);
        return {
            index: i,
            name: MATRIX_QUAD_NAMES[i],
            count: counts[i],
            ratio: ratioOf(counts[i]),
            namesText: names.join('、'),
            visible: counts[i] > 0,
            trait: copy.trait,
            advice: copy.advice,
        };
    };
    return {
        total,
        maxName: joinQuadNames(maxOnes.map((e) => e.i)),
        maxRatio: maxOnes[0].ratio,
        minName: joinQuadNames(minOnes.map((e) => e.i)),
        minRatio: minOnes[0].ratio,
        quads: [1, 2, 3, 4].map(makeQuad),
    };
}

/** ownexam 中 create_at 最新；相同时 id 更大优先 */
export function pickLatestOwnexamItem(list) {
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.reduce((best, item) => {
        if (!item) return best;
        if (!best) return item;
        const bestTime = new Date(best.create_at).getTime();
        const itemTime = new Date(item.create_at).getTime();
        if (Number.isNaN(itemTime)) return best;
        if (Number.isNaN(bestTime) || itemTime > bestTime) return item;
        if (itemTime === bestTime && Number(item.id) > Number(best.id)) return item;
        return best;
    }, null);
}
